import * as THREE from 'three';
import * as SavaneJS from '@rhinov/savane-js';
import { WebglScene } from "../webgl/scene";
import { WebglFurniture } from '../webgl/furniture';

declare var Savane;

export class SmartContentHandler {

    private divWebgl: HTMLElement;
    private scene: WebglScene;

    constructor(divWebgl: HTMLElement, scene: WebglScene) {
        this.divWebgl = divWebgl;
        this.scene = scene;
    }

    SelectArrangement = (point: {x: number, y: number}) : void => {
        let mousePosition = new THREE.Vector2(point.x, point.y);
        let camera = this.scene.getActiveGLCamera() as THREE.PerspectiveCamera;
        this.scene.rayCaster.setFromCamera(mousePosition, camera);
        let intersects = this.scene.rayCaster.intersectObjects(this.scene.threeScene.children, true);
        intersects = this.scene.removeGizmoFromIntersectionResults(intersects, true);

        if (intersects.length === 0) {
            return;
        }

        // Try to get the savane entity id stored into the userdata of the object
        let id = intersects[0].object.parent.userData.id;

        // This is for objects that do not have VLP they'll only have the default mesh instead of the vlp itself, do not search for parent then
        if (id === undefined) {
            id = intersects[0].object.userData.id;
        }

        if (id !== undefined) {
            let node = this.scene.getPlanEntity(id) as WebglFurniture;
            Savane.eventsManager.instance.dispatch(SavaneJS.Events.SELECTED_ARRANGEMENT, { entity: node.entity, point: point });
        }
    }

    SelectCoating = (point: {x: number, y: number}) : boolean => {
        let mousePosition = new THREE.Vector2(point.x, point.y);
        let camera = this.scene.getActiveGLCamera() as THREE.PerspectiveCamera;
        this.scene.rayCaster.setFromCamera(mousePosition, camera);
        let intersects = this.scene.rayCaster.intersectObjects(this.scene.threeScene.children, true);
        intersects = this.scene.removeGizmoFromIntersectionResults(intersects, true);

        if (intersects.length === 0) {
            return false;
        }

        let intersect = intersects[0];
        // hull entity
        let entity = null;
        let childs = [];
        if (!entity && this.scene.floorGeneratorHull) {
            entity = this.scene.floorGeneratorHull.getEntityFromChild(intersect.object);
            if (entity) {
                childs = this.scene.floorGeneratorHull.getChildsFromId(entity.id);
            }
        }
        if (!entity && this.scene.dynamicHull) {
            entity = this.scene.dynamicHull.getEntityFromChild(intersect.object);
            if (entity) {
                childs = this.scene.dynamicHull.getChildsFromId(entity.id);
            }
        }
        if (!entity && this.scene.staticHull) {
            entity = this.scene.staticHull.getEntityFromChild(intersect.object);
            if (entity) {
                childs = this.scene.staticHull.getChildsFromId(entity.id);
            }
        }

        let result = false;
        if (entity && (entity.isWallEntity() || entity.isRoomEntity() ||
                entity.isGeometryPrimitiveEntity() || entity.isFloorEntity())) {

            let coatingArea = intersect.object.name.startsWith("CoatingArea");
            // coating
            let hangType = intersect.object.name.indexOf("Direct") !== -1 ? SavaneJS.Coating.HangType.wallDirect : SavaneJS.Coating.HangType.wallUndirect;
            if (entity.isWallEntity()) {
                if (intersect.object.name.indexOf("Slope") !== -1) {
                    if (intersect.object.name.indexOf("Direct") === -1) {
                        hangType = SavaneJS.Coating.HangType.slopeUndirect;
                    }
                    else {
                        hangType = SavaneJS.Coating.HangType.slopeDirect;
                    }
                }
            } else if (entity.isRoomEntity() || entity.isFloorEntity()) {
                hangType = intersect.object.name.indexOf("axo") !== -1 ? SavaneJS.Coating.HangType.ceiling : SavaneJS.Coating.HangType.floor;
            } else if (entity.isGeometryPrimitiveEntity()) {
                hangType = SavaneJS.Coating.HangType.technicalElement;
            }
            if (coatingArea) {
                let coatingAreaParse = intersect.object.name.split('_');
                let coatingAreaNb = Number(coatingAreaParse[2]) - 1;
                let coatings = entity.getComponents(SavaneJS.ComponentConstants.ComponentType.CoatingArea);
                Savane.eventsManager.instance.dispatch(SavaneJS.Events.SELECTED_COATING, { entity: entity, coating: coatings[coatingAreaNb], point: point });
                result = true;
            }
            else if ((intersect.object as any).coatingId) {
                //Check coating
                let coatings = [];
                if (coatingArea == false) {
                    coatings = coatings.concat(entity.getComponents(SavaneJS.ComponentConstants.ComponentType.Coating));
                }
                if (coatings.length == 0) {
                    coatings = entity.getComponents(SavaneJS.ComponentConstants.ComponentType.FloorCoatingArea);
                }
                if ((coatings !== null) && (coatings.length > 0)) {
                    for (let i = 0; i < coatings.length; i++) {
                        if ((intersect.object as any).coatingId !== coatings[i].coatingId) {
                            continue;
                        }
                        if (coatings[i].hangType !== hangType) {
                            continue;
                        }
                        Savane.eventsManager.instance.dispatch(SavaneJS.Events.SELECTED_COATING, { entity: entity, coating: coatings[i], point: point });
                        result = true;
                        break;
                    }
                }
            } else if (entity.isGeometryPrimitiveEntity()) {
                let coatings = entity.getComponents(SavaneJS.ComponentConstants.ComponentType.Coating);
                if ((coatings !== null) && (coatings.length > 0)) {
                    for (let i = 0; i < coatings.length; i++) {
                        if (coatings[i].hangType !== hangType && coatings[i].hangType !== SavaneJS.Coating.HangType.usemtl) {
                            continue;
                        }
                        Savane.eventsManager.instance.dispatch(SavaneJS.Events.SELECTED_COATING, { entity: entity, coating: coatings[i], point: point });
                        result = true;
                        break;
                    }
                }
            }
        }
        return result;
    }

    HandleMouseDown = (event: MouseEvent) : void => {
        if (event.button === 0) {
            let coatingSelected = this.SelectCoating({
                x: (event.offsetX / (event.target as HTMLElement).clientWidth) * 2 - 1,
                y: (((event.target as HTMLElement).clientHeight - event.offsetY) / (event.target as HTMLElement).clientHeight) * 2 - 1,
            });
            if (!coatingSelected) {
                this.SelectArrangement({
                    x: (event.offsetX / (event.target as HTMLElement).clientWidth) * 2 - 1,
                    y: (((event.target as HTMLElement).clientHeight - event.offsetY) / (event.target as HTMLElement).clientHeight) * 2 - 1,
                });
            }
        }
    }
}
